import React, { useEffect, useState } from "react";
import { Printer } from "lucide-react";
import { Modal } from "./Dialogs";

const MODES = [
  ["actual", "True size", "Prints at the exact size of the design (A4 designs fill the sheet exactly)."],
  ["fit", "Best fit", "Shrinks or enlarges to fit the whole design on the sheet — nothing is cut off."],
  ["fill", "Fill page", "Fills the whole sheet edge to edge — the edges may be trimmed."],
];
const MM = 25.4 / 96;

function printImage(url, size, mode, land) {
  const pw = land ? 297 : 210, ph = land ? 210 : 297;
  const img = mode === "actual" ? `width:${(size.w * MM).toFixed(2)}mm;height:${(size.h * MM).toFixed(2)}mm;flex-shrink:0` : `width:100%;height:100%;object-fit:${mode === "fit" ? "contain" : "cover"}`;
  const f = document.createElement("iframe");
  f.style.cssText = "position:fixed;right:0;bottom:0;width:0;height:0;border:0";
  document.body.appendChild(f);
  const d = f.contentDocument;
  d.open();
  d.write(`<!doctype html><html><head><title>Print</title><style>@page{size:A4 ${land ? "landscape" : "portrait"};margin:0}html,body{margin:0;padding:0;height:${ph}mm;overflow:hidden}
    .pg{width:${pw}mm;height:${ph}mm;overflow:hidden;display:flex;align-items:center;justify-content:center}img{display:block;${img}}</style></head>
    <body><div class="pg"><img id="im" src="${url}"/></div></body></html>`);
  d.close();
  const go = () => { f.contentWindow.focus(); f.contentWindow.print(); setTimeout(() => f.remove(), 1500); };
  const im = d.getElementById("im");
  im.complete ? go() : (im.onload = go);
}

export function PrintDialog({ s, onClose }) {
  const [mode, setMode] = useState("actual");
  const land = s.size.w > s.size.h;
  const [url, setUrl] = useState("");
  useEffect(() => { setUrl(s.exportUrl("png")); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const pw = land ? 297 : 210, ph = land ? 210 : 297, k = 220 / Math.max(pw, ph);
  const prev = mode === "actual" ? { width: s.size.w * MM * k, height: s.size.h * MM * k, maxWidth: "none", flexShrink: 0 } : { width: "100%", height: "100%", objectFit: mode === "fit" ? "contain" : "cover" };
  const doPrint = () => { printImage(url, s.size, mode, land); onClose(); };
  return (
    <Modal title="Print" onClose={onClose} testid="ds-print-dialog" width="max-w-2xl">
      <div className="flex gap-4">
        <div className="shrink-0 grid place-items-center bg-slate-200 rounded-md p-3" style={{ width: 250, height: 250 }}>
          <div className="bg-white shadow-md overflow-hidden flex items-center justify-center" style={{ width: pw * k, height: ph * k }} data-testid="ds-print-preview">
            {url && <img src={url} alt="" style={prev} />}</div>
        </div>
        <div className="flex-1 space-y-2 text-[13px]">
          <div className="text-[12px] text-slate-600" data-testid="ds-print-paper">Paper: <b>A4 {land ? "Landscape" : "Portrait"}</b> (matches your design)</div>
          {MODES.map(([k2, t, d]) => (
            <label key={k2} data-testid={`ds-print-mode-${k2}`} className={`flex gap-2 p-2.5 rounded-md border cursor-pointer transition-colors ${mode === k2 ? "border-[#21a6ec] bg-sky-50" : "border-slate-200 hover:bg-slate-50"}`}>
              <input type="radio" name="pmode" checked={mode === k2} onChange={() => setMode(k2)} className="mt-0.5 accent-[#21a6ec]" />
              <span><b>{t}</b><br /><span className="text-[11px] text-slate-500">{d}</span></span>
            </label>
          ))}
          <div className="text-[11px] text-slate-500">Tip: in the print window, set Margins to "None" and turn off "Headers and footers" for a clean edge.</div>
          <div className="flex justify-end gap-2 pt-1">
            <button onClick={onClose} className="px-3 py-2 rounded-md text-[13px] font-bold bg-slate-100 hover:bg-slate-200">Cancel</button>
            <button onClick={doPrint} disabled={!url} data-testid="ds-print-confirm" className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md text-[13px] font-bold bg-[#0b6623] text-white hover:bg-[#08501b]"><Printer className="h-4 w-4" />Print</button>
          </div>
        </div>
      </div>
    </Modal>
  );
}
